import { IDocumentFileModel, IExternalLinkForm, IExternalLinkModel } from '@api/models';
import { renameDocumentFile, renameExternalLinkDocument } from '@api/services/document.service';
import { LoadingButton } from '@components/buttons';
import { Button, Drawer, Label } from '@hyperflake/react-ui-library';
import { FormHelperText } from '@library/forms';
import { useMutation } from '@tanstack/react-query';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput } from 'modules/shared/features/formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

interface RenameExternalFileProps {
    clientId: string;
    show: boolean;
    onClose: () => void;
    selectedExternalLink: IExternalLinkModel;
    refetchAll?: () => void;
}

const RenameExternalLinkSchema = Yup.object({
    name: Yup.string().trim().required('This field is required'),
});

const RenameExternalFileDrawer = (props: RenameExternalFileProps) => {
    const { show, onClose, selectedExternalLink, clientId, refetchAll } = props;

    const initialValues = {
        name: selectedExternalLink?.name ?? '',
    };

    const renameMutation = useMutation({
        mutationFn: (payload: { name: string }) =>
            renameExternalLinkDocument(clientId, selectedExternalLink._id, payload),
        onSuccess: (updatedFile) => {
            toast.success('External Link renamed successfully.');
            // onSuccess?.(updatedFile);
            refetchAll();
            onClose();
        },
        onError: (err: any) => {
            toast.error(err?.data?.message || err?.message || 'Some error occurred, please try again.');
        },
    });

    const handleSubmit = async (values: { name: string }) => {
        const name = values.name.trim();
        renameMutation.mutate({ name });
    };

    return (
        <Drawer open={show} onClose={onClose}>
            <div className="flex flex-col h-[calc(100dvh-3rem)]">
                <h4>Rename External Link</h4>

                <div className="mt-6">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={RenameExternalLinkSchema}
                        enableReinitialize
                        onSubmit={handleSubmit}
                    >
                        <Form id="renameExternalLinkForm" className="flex flex-col gap-2">
                            <div>
                                <Label htmlFor="name">Name</Label>
                                <FormikInput name="name" placeholder="Enter file name" />

                                <FormHelperText variant="error">
                                    <ErrorMessage name="name" />
                                </FormHelperText>
                            </div>
                        </Form>
                    </Formik>
                </div>

                <div className="mt-auto">
                    <div className="flex gap-4 mt-6">
                        <Button type="button" size="sm" color="secondary" className="font-medium" onClick={onClose}>
                            Cancel
                        </Button>

                        <LoadingButton
                            type="submit"
                            size="sm"
                            form="renameExternalLinkForm"
                            isLoading={renameMutation.isPending}
                            loadingText="Saving"
                        >
                            Save
                        </LoadingButton>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default RenameExternalFileDrawer;
